/**
 * Why you are looking at a sign-in page (G5).
 *
 * Whatever ended the session leaves a note (lib/sessionEnd.ts) and this reads
 * it on the sign-in page. It replaced an eight-second toast, which had two
 * problems: it was gone before somebody who had walked back to their desk had
 * found their glasses, and it was drawn in the shape this app uses for "that
 * failed" — when an idle sign-out is not a failure, it is the security posture
 * working.
 *
 * So it is a nudge card that STAYS (components/ui/nudge-card.tsx, first in the
 * queue in lib/nudgeQueue.ts): no timer, no ✕, no "Not now" — there is nothing
 * to refuse, and dismissing it by accident would hide the only explanation on
 * the screen. It retires when the reader presses "Got it" (the form is right
 * there; the card is what is in its way) or when they sign in (AuthContext
 * calls clearSessionEndedNotice()). Its button is the ordinary primary, not
 * red: red on a button reads as a warning about what you are about to press.
 *
 * Rendered by the sign-in page itself (Login.tsx), so nothing at the app root
 * needs to mount it.
 *
 * The note is read once per page load and held here at module scope, not in
 * component state: the sign-in page unmounts and remounts whenever the auth
 * state flips (AuthContext swaps the tree for a loader), and a note already
 * consumed from storage would otherwise vanish on the first remount.
 */
import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { NudgeCard, NudgeTile } from "@/components/ui/nudge-card";
import { useNudge } from "@/lib/nudgeQueue";
import { useTranslation } from "@/hooks/useTranslation";
import { IDLE_TIMEOUT_MINUTES } from "@shared/sessionPolicy";
import { hasReturnTo, takeSessionEnd, type SessionEndReason } from "@/lib/sessionEnd";

/** undefined = not read yet this page load; null = nothing to explain. */
let current: SessionEndReason | null | undefined;
const listeners = new Set<() => void>();

function readNotice(): SessionEndReason | null {
  // A fresh note (another sign-out since the last one was dismissed) wins.
  const fresh = takeSessionEnd();
  if (fresh) current = fresh;
  else if (current === undefined) current = null;
  return current ?? null;
}

/** Retire the card — the reader signed in. */
export function clearSessionEndedNotice(): void {
  current = null;
  listeners.forEach((l) => l());
}

export function SessionEndedCard({ placement = "fixed" }: { placement?: "fixed" | "inline" }) {
  const t = useTranslation();
  const [reason, setReason] = useState<SessionEndReason | null>(() => (current === undefined ? null : current ?? null));
  const [goingBack, setGoingBack] = useState(false);

  useEffect(() => {
    setReason(readNotice());
    setGoingBack(hasReturnTo());
    const onClear = () => setReason(null);
    listeners.add(onClear);
    return () => {
      listeners.delete(onClear);
    };
  }, []);

  const mine = useNudge("session", reason !== null);
  if (!reason || !mine) return null;

  const copy: Record<SessionEndReason, { title: string; body: string }> = {
    idle: {
      title: t("signedOutTitle", "Signed Out for Your Security"),
      body: t("signedOutIdleDescription", "You were inactive for {minutes} minutes, so we ended your session. Please sign in again.").replace("{minutes}", String(IDLE_TIMEOUT_MINUTES)),
    },
    expired: { title: t("signedOutTitle", "Signed Out for Your Security"), body: t("signedOutExpiredDescription", "Your session reached its maximum length and was ended. Please sign in again.") },
    // Deliberately silent about inactivity: the server ended it for a reason
    // it did not give, which happens mid-task as readily as after a pause.
    ended: {
      title: t("shellAuthSessionEndedTitle", "Your Session Ended"),
      body: t("shellAuthSessionEndedBody", "For your security, SaleFish closed your session. Sign in again to carry on."),
    },
    elsewhere: {
      title: t("shellAuthSignedOutElsewhereTitle", "You Signed Out in Another Tab"),
      body: t("shellAuthSignedOutElsewhereBody", "Signing out applies to every SaleFish tab in this browser. Sign in again to carry on."),
    },
  };
  const { title, body } = copy[reason];

  return (
    <NudgeCard
      lead={<NudgeTile icon={ShieldCheck} />}
      title={title}
      body={goingBack ? t("shellAuthSessionEndedGoingBack", "{body} You'll go straight back to where you were.", { body }) : body}
      primary={{
        label: t("shellAuthGotIt", "Got it"),
        onClick: () => {
          current = null;
          setReason(null);
        },
      }}
      placement={placement}
      testId="card-session-ended"
    />
  );
}
